import {
  IconButton,
  Text,
  IconCommentLine,
  IconCommentsOffLine,
  IconAssignmentLine,
  Flex,
  View,
  IamProtected,
} from '@smarterservices/smarter-ui';
import ReviewerTools from './ReviewerTools';
import ReviewerComments from './ReviewerComments';
import AnnotationDetails from './AnnotationDetails';
import DeleteAnnotationButton from './ReviewerTools/DeleteAnnotationButton';
import ApproveAnnotationButton from './ReviewerTools/ApproveAnnotationButton';
import ViewAnnotationButton from './ReviewerTools/ViewAnnotationButton';
import { useState, useRef, useCallback, memo, useEffect } from 'react';
import CONSTANTS from '../../../config/constants';
import { getStore } from '../../../stores/StoreContext';
import AdjustAnnotationButton from './ReviewerTools/AdjustAnnotationButton';
const LineDetail = memo(({ annotation, hideMenu, reviewerTools, details, isHovered = false, showDetailsEditor, onToggleDetailsEditor, hasDetails, onHeightChange }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [showReviewerComments, setShowReviewerComments] = useState(false);
  const [hasReviewerComments, setHasReviewerComments] = useState(false);
  const [deleteConfirmMode, setDeleteConfirmMode] = useState(false);
  const menuTriggerRef = useRef(null);
  const reviewerCommentsRef = useRef(null);
  const ExamSessionStore = getStore('examSessionStore');
  const PlayerStore = getStore('playerStore');
  const AppStore = getStore('appStore');

  // Memoize handlers to prevent recreation on each render
  const handleDelete = useCallback(() => {
    if (annotation) {
      ExamSessionStore.deleteAnnotation(annotation.sid);
      setShowMenu(false);
    }
  }, [annotation, ExamSessionStore]);

  const handleJumpTo = useCallback(() => {
    if (annotation) {
      PlayerStore.jumpTo(
        annotation.start > CONSTANTS.ANNOTATIONS.SECONDS_LEADING_TO_ANNOTATION
          ? annotation.start -
          CONSTANTS.ANNOTATIONS.SECONDS_LEADING_TO_ANNOTATION
          : 0
      );
      setShowMenu(false);
    }
  }, [annotation]);

  const handleView = useCallback(() => {
    if (annotation) {
      // Use a safer approach to avoid ref issues
      // First close the menu to prevent any potential ref conflicts
      setShowMenu(false);

      // Use a small timeout to ensure the menu is fully closed before setting the annotation
      setTimeout(() => {
        ExamSessionStore.setManagingAnnotation(annotation);
      }, 50);
    }
  }, [annotation, ExamSessionStore]);

  useEffect(() => {
    if (annotation) {
      setHasReviewerComments(annotation?.reviewerComment && annotation?.reviewerComment !== '');
    }
  }, [annotation]);

  // Trigger height recalculation when transcription appears
  useEffect(() => {
    if (annotation?.metadata?.transcription && onHeightChange) {
      onHeightChange();
    }
  }, [annotation?.metadata?.transcription, onHeightChange]);

  // If no annotation is available, don't render anything
  if (!annotation) {
    return null;
  }

  // Check if there's a transcript to show
  const hasTranscript = annotation.metadata?.transcription;

  // Only render if there's something to show: details, tools, or transcript
  if (!details && !reviewerTools && !hasTranscript) {
    return null;
  }

  return (
    <View
      as="div"
      padding="x-small none"
      position="relative"
    >
      {reviewerTools && (
        <View as="div"
          className="annotation-tools"
          background="primary-inverse"
          borderWidth="small"
          borderColor="primary"
          borderRadius="medium"
          padding="xx-small"
        >
          <Flex justifyItems="space-between">
            <Flex.Item>

              <ApproveAnnotationButton
                annotation={annotation}
              />

              <AdjustAnnotationButton
                annotation={annotation}
              />

              <IamProtected
                action="sp:ModifyExamAnnotation"
                resource={AppStore.getIamResourceString(`session/${ExamSessionStore.sessionSid}`)}
              >
                {!hasDetails && (
                  <IconButton
                    size="small"
                    margin="none x-small none none"
                    screenReaderLabel={showDetailsEditor ? "Hide details editor" : "Show details editor"}
                    onClick={onToggleDetailsEditor}
                    color={showDetailsEditor ? "success" : "primary"}
                  >
                    <IconAssignmentLine />
                  </IconButton>
                )}
              </IamProtected>
              <DeleteAnnotationButton
                annotation={annotation}
              />
              {/* 
              TODO: For launch we are going to hide these...
              <IamProtected
                action="sp:ModifyExamAnnotation"
                resource={AppStore.getIamResourceString(`session/${ExamSessionStore.sessionSid}`)}
              >

                {(!hasReviewerComments && !showReviewerComments) && (
                  <IconButton
                    size="small"
                    margin="none x-small none none"
                    screenReaderLabel="Edit Annotation"
                    onClick={() => {
                      setShowReviewerComments(true);
                      setTimeout(() => {
                        if (reviewerCommentsRef.current) {
                          reviewerCommentsRef.current.startEditing();
                        }
                      }, 100);
                    }}
                  >
                    <IconCommentLine />
                  </IconButton>
                )}
                
                {(!hasReviewerComments && showReviewerComments) && (
                  <IconButton
                    size="small"
                    margin="none x-small none none"
                    screenReaderLabel="Edit Annotation"
                    onClick={() => setShowReviewerComments(false)}
                  >
                    <IconCommentsOffLine />
                  </IconButton>
                )}
                
              </IamProtected>
              */}

              <ViewAnnotationButton
                annotation={annotation}
              />

            </Flex.Item>
          </Flex>
          {((annotation?.reviewerComment && annotation?.reviewerComment !== '') || showReviewerComments) && (
            <View as="div" margin="small none none none">
              <Text variant="legend">The comments entered below are only visible to the proctoring team.</Text>
              <ReviewerComments
                ref={reviewerCommentsRef}
                annotation={annotation}
              />
            </View>

          )}
        </View>
      )}
      <Flex>
        <Flex.Item shouldGrow>
          {/* Always show transcript for speech/noise detected flags */}
          {annotation.metadata?.transcription && (
            <View as="div"
              padding="x-small"
              background="secondary"
              borderRadius='medium'
              borderWidth='small'>
              <Text as="div" variant="contentQuote" themeOverride={{ content: '0.8rem' }}>
                {annotation?.metadata.transcription}
              </Text>
            </View>
          )}

          {/* Show details section based on toggle state */}
          {(details || hasDetails || showDetailsEditor) && (
            <View as="div" className="annotation-details" >
              <AnnotationDetails
                annotation={annotation}
                onHeightChange={onHeightChange}
              />
            </View>
          )}
        </Flex.Item>
      </Flex>
    </View>
  );
});

export default LineDetail;
